import { useState, useEffect, useCallback } from 'react';
import { Appointment } from '../types/multiTenant';
import appointmentService from '../services/appointmentService';
import { useOrganization } from '../contexts/OrganizationContext';

interface AppointmentFilters {
  clientId?: string;
  assignedTo?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Hook for managing appointments
 * Automatically loads appointments for the current organization
 */
export const useAppointments = (filters?: AppointmentFilters) => {
  const { organization, currentUser } = useOrganization();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load appointments with filters
   */
  // Dependency em organization?.id (primitivo) em vez do objeto inteiro.
  // Ver explicação detalhada em useClients.ts e OrganizationContext.tsx.
  const loadAppointments = useCallback(async () => {
    if (!organization) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await appointmentService.listAppointments(organization.id, filters);
      setAppointments(data);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, filters]);

  /**
   * Create new appointment.
   *
   * THROWS on any failure (missing context, network/Supabase error).
   * Previous version returned null silently, which let callers show a
   * fake success toast while the appointment was never persisted.
   */
  const createAppointment = useCallback(
    async (
      data: Omit<Parameters<typeof appointmentService.createAppointment>[0], 'organizationId' | 'createdBy'>
    ) => {
      if (!organization || !currentUser) {
        // Não chamamos setError aqui: o `error` deste hook é exposto via
        // RealtimeDataContext e o wrapper pages/Calendar.tsx mostra tela
        // de erro de carregamento ao ver qualquer erro. Errors de ação
        // são responsabilidade do caller (Calendar component) via snackbar.
        throw new Error('Aguarde o sistema carregar antes de criar a consulta. Tente novamente em alguns segundos.');
      }

      setIsLoading(true);

      try {
        const newAppointment = await appointmentService.createAppointment({
          ...data,
          organizationId: organization.id,
          createdBy: currentUser.id,
        });

        if (newAppointment) {
          setAppointments((prev) => [...prev, newAppointment]);
        }

        return newAppointment;
      } catch (err) {
        console.error('Error creating appointment:', err);
        // Não setError — manteria como "erro de load" no contexto.
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [organization, currentUser]
  );

  /**
   * Update appointment
   */
  const updateAppointment = useCallback(
    async (appointmentId: string, updates: Partial<Appointment>) => {
      setIsLoading(true);
      setError(null);

      try {
        const updated = await appointmentService.updateAppointment(appointmentId, updates);

        if (updated) {
          setAppointments((prev) =>
            prev.map((apt) => (apt.id === appointmentId ? updated : apt))
          );
        }

        return updated;
      } catch (err) {
        console.error('Error updating appointment:', err);
        // Não setError — caller trata via snackbar.
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Delete appointment
   */
  const deleteAppointment = useCallback(async (appointmentId: string) => {
    setIsLoading(true);

    try {
      const success = await appointmentService.deleteAppointment(appointmentId);

      if (success) {
        setAppointments((prev) => prev.filter((apt) => apt.id !== appointmentId));
      }

      return success;
    } catch (err) {
      console.error('Error deleting appointment:', err);
      // Não setError — caller trata via snackbar.
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Mark appointment as completed
   */
  const completeAppointment = useCallback(async (appointmentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await appointmentService.completeAppointment(appointmentId);

      if (success) {
        // Reload to get updated data
        await loadAppointments();
      }

      return success;
    } catch (err) {
      console.error('Error completing appointment:', err);
      setError('Failed to complete appointment');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadAppointments]);

  /**
   * Mark appointment as no-show
   */
  const markAsNoShow = useCallback(async (appointmentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await appointmentService.markAsNoShow(appointmentId);

      if (success) {
        // Reload to get updated data
        await loadAppointments();
      }

      return success;
    } catch (err) {
      console.error('Error marking as no-show:', err);
      setError('Failed to mark as no-show');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadAppointments]);

  /**
   * Get appointments by client
   */
  const getAppointmentsByClient = useCallback(async (clientId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await appointmentService.getAppointmentsByClient(clientId);
      return data;
    } catch (err) {
      console.error('Error getting appointments by client:', err);
      setError('Failed to get appointments');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get appointments by date range
   */
  const getAppointmentsByDateRange = useCallback(
    async (startDate: string, endDate: string) => {
      if (!organization) return [];

      setIsLoading(true);
      setError(null);

      try {
        const data = await appointmentService.getAppointmentsByDateRange(
          organization.id,
          startDate,
          endDate
        );
        return data;
      } catch (err) {
        console.error('Error getting appointments by date range:', err);
        setError('Failed to get appointments');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [organization]
  );

  /**
   * Get appointment statistics
   */
  const getAppointmentStats = useCallback(
    async (startDate?: string, endDate?: string) => {
      if (!organization) return null;

      try {
        const stats = await appointmentService.getAppointmentStats(
          organization.id,
          startDate,
          endDate
        );
        return stats;
      } catch (err) {
        console.error('Error getting appointment stats:', err);
        return null;
      }
    },
    [organization]
  );

  /**
   * Refresh appointments
   */
  const refresh = useCallback(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Auto-load on mount and when organization or filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (organization?.id) {
      loadAppointments();
    }
  }, [organization?.id, filters]);

  return {
    // Data
    appointments,
    isLoading,
    error,

    // Actions
    createAppointment,
    updateAppointment,
    deleteAppointment,
    completeAppointment,
    markAsNoShow,
    getAppointmentsByClient,
    getAppointmentsByDateRange,
    getAppointmentStats,
    refresh,
  };
};

export default useAppointments;
